import { Label } from '@/Models/GenericWorklist/Label';
import { DobButton, DobDropdownMenu, DobDropdownMenuArrow, DobDropdownMenuItem } from '@dedalusdiit/dob-components';
import { t } from 'i18next';
import Styles from './WorklistFooter.module.scss';
import { WorklistSelectionActionsOverflowMenu } from './WorklistSelectionActionsOverflowMenu';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import { IWorklistStore } from '@/Pages/GenericWorklist/Stores/WorklistStore';
import * as API from '@/API';
import { useNavigate } from 'react-router';
import { navigateToReport } from '@/Pages/GenericWorklist/Logic/ReportNavigation';
import { useSystemSettingsContext } from '@/Contexts/App/SystemSettingsContext';
import BulkType from '@/Models/Reports/BulkType';

const {
    actionButtons: actionButtonsStyle,
    scrollableContent: scrollableContentStyle,
} = Styles;

export interface WorklistSelectionActionsProps {
    allLabels: Label[] | [];
    assignLabelToOrders: (labelId: number) => Promise<void>;
    handleMarkAsReady: () => Promise<void>;
    labelsAssignedToSelectedOrders: Label[] | [];
    removeLabelFromOrders: (labelId: number) => Promise<void>;
    worklistStore: IWorklistStore;
    anySelected: boolean;
}

export const WorklistSelectionActions = (props: WorklistSelectionActionsProps): React.ReactElement => {
    const {
        allLabels,
        assignLabelToOrders,
        handleMarkAsReady,
        labelsAssignedToSelectedOrders,
        removeLabelFromOrders,
        anySelected,
        worklistStore,
    } = props;

    const { user } = useUserContext();
    const navigate = useNavigate();
    const settingsContext = useSystemSettingsContext();
    const reportController = settingsContext.getValue('ReportController')!;

    const startReporting = async (): Promise<void> => {
        if (worklistStore.selectedIds.length == 0) {
            console.warn('Not starting reporting, because no valid ids were selected');
            return;
        }

        if (worklistStore.selectedIds.length > 1) {
            const actions = worklistStore.classifySelectedOrders();
            const res = await API.worklist.startBulkSession(actions);
            if (!res.AllOk) {
                console.error('Start Bulk Reporting error:', res.Message);
                return;
            }

            // In case of bulk dictation, we assume here that the order of the ids stays the same in the session on the server
            // So without having to call any additional API we can just start with the first item in this client-side list.
            if (actions[0].type === 'validate') {
                // Navigate to report with correct url?
                navigateToReport(
                    navigate,
                    actions[0].orderId,
                    actions[0].reportId,
                    reportController,
                    undefined,
                    true,
                    BulkType.Mixed,
                );
            }
            else {
                await worklistStore.openReportDialog(actions[0].orderId);
            }
            return;
        }

        await worklistStore.openReportDialog(worklistStore.selectedIds[0]);
    };

    return (
        <div className={actionButtonsStyle}>
            {
                user.canCreateAndEditReport
                && (
                    <DobButton
                        disabled={!anySelected || (reportController == 'Order' && worklistStore.selectedIds.length > 1)} // 13/08/2026 (1.9.3.0): Added a specific Disable option for Order ReportController to block bulk reporting with that Controller for now! This should be removed once we fix this behavior!
                        onClick={() => void startReporting()}
                    >
                        {t('Report')}
                    </DobButton>
                )
            }

            <DobDropdownMenu
                side="top"
                sideOffset={5}
                disabled={!anySelected}
                trigger={(
                    <div>
                        <DobButton disabled={!anySelected} color="primary">
                            {t('Label')}
                        </DobButton>
                    </div>
                )}
            >
                <div className={scrollableContentStyle}>
                    {allLabels.length > 0
                        && allLabels.map((label) => (
                            <DobDropdownMenuItem title={t('AssignLabelToAllSelected')} key={label.ID} onClick={() => void assignLabelToOrders(label.ID)}>
                                {label.Name}
                            </DobDropdownMenuItem>
                        ))}
                </div>
                <DobDropdownMenuArrow />
            </DobDropdownMenu>

            <WorklistSelectionActionsOverflowMenu
                handleMarkAsReady={handleMarkAsReady}
                labelsAssignedToSelectedOrders={labelsAssignedToSelectedOrders}
                removeLabelFromOrders={removeLabelFromOrders}
                anySelected={anySelected}
            />
        </div>
    );
};

export default WorklistSelectionActions;
